import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Posición de un menú desplegable anclado a su botón. Salió de `CustomSelect` cuando
 * `CustomMultiSelect` necesitó exactamente lo mismo (ADR-2026-10-19-permisos-por-miembro-del-equipo).
 *
 * El menú se renderiza en un PORTAL a <body> (no como hijo absolute), porque el panel de
 * contenido (`.content-panel-clip` en App) tiene `clip-path`, que recorta cualquier
 * descendiente (incluso `fixed`). Al portalearlo a body queda fuera de ese clip. Como es
 * `fixed`, la posición sale del rect del botón y se recalcula en scroll/resize. Un click
 * afuera del botón y del menú lo cierra.
 */
export type MenuPos = {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
};

export function useMenuAnclado(open: boolean, setOpen: (v: boolean) => void) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<MenuPos | null>(null);

  const updatePosition = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    // Si abajo no entra bien y arriba hay más lugar, se abre hacia arriba.
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    // Tope de alto: ~9 ítems visibles (el resto scrollea). Sin esto, en pantallas
    // altas mostraría las 24 zonas de una y queda larguísimo.
    const MAX_MENU_HEIGHT = 360;
    const avail = openUp ? spaceAbove : spaceBelow;
    setPos({
      left: r.left,
      width: r.width,
      top: openUp ? undefined : r.bottom + gap,
      bottom: openUp ? window.innerHeight - r.top + gap : undefined,
      maxHeight: Math.min(MAX_MENU_HEIGHT, Math.max(160, avail)),
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    // `capture` para agarrar también el scroll del panel de contenido en mobile.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      // No cerrar si el click cae en el botón/wrapper o dentro del menú portaleado.
      if (wrapRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { wrapRef, btnRef, menuRef, pos };
}
